import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import {
  OPEN, listViolations, reviewViolation, sourceLabel, personName, personLink,
  conversationLink, isSupplierRole,
} from '../../lib/consoleContactViolations';

// ── Contact violations ─────────────────────────────────────────────────────
//
// Every phone number, WeChat, email or link the platform masked before it
// reached the other side (owner, 2026-10-15). The masking already happened —
// this page is where the team decides what it meant:
//   • تجاهل  — a false positive (a model number, a price that looked like a
//              phone). It stops counting toward the user's warning level.
//   • تحذير  — the user is sent the formal warning in their own language,
//              as an in-app notification (and a push, on app 1.1.3+).
// Reached from Reviews and from the Notifications feed; no sidebar entry of its
// own, per the note on NAV in ConsoleShell.jsx.

const FILTERS = [
  { key: OPEN, ar: 'بانتظار المراجعة', en: 'Needs review' },
  { key: 'warned', ar: 'حُذِّر', en: 'Warned' },
  { key: 'dismissed', ar: 'تُجوهل', en: 'Dismissed' },
  { key: 'all', ar: 'الكل', en: 'All' },
];

// What the user receives on «أرسل تحذير رسمي». The text itself lives in
// admin_review_contact_event (migration 20261014163439); this is the Arabic
// copy shown in the confirm dialog so the admin knows what is being sent.
// If the RPC's wording changes, change this too.
const WARNING_PREVIEW = {
  supplier: 'تحذير من معبر: يُمنع طلب التواصل أو تحويل التعاملات خارج منصة معبر. تكرار هذه المخالفة قد يؤدي إلى تعليق حسابك أو حظره نهائيًا.',
  buyer: 'تنبيه من معبر: حفاظًا على حقوقك، أبقِ جميع المحادثات والاتفاقيات داخل منصة معبر. معبر غير مسؤول عن التعاملات أو الاتفاقيات التي تتم خارج المنصة.',
};

const STATUS_BADGE = {
  open: { ar: 'بانتظار المراجعة', en: 'Open', tone: 'warn' },
  warned: { ar: 'حُذِّر', en: 'Warned', tone: 'danger' },
  dismissed: { ar: 'تُجوهل', en: 'Dismissed', tone: 'neutral' },
};

const when = (iso, isAr) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(isAr ? 'ar-SA-u-nu-latn' : 'en-GB', {
      timeZone: 'Asia/Riyadh', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return String(iso).slice(0, 16).replace('T', ' '); }
};

const textBox = {
  margin: 0, padding: '8px 10px', borderRadius: 8, fontSize: 13, lineHeight: 1.6,
  whiteSpace: 'pre-wrap', wordBreak: 'break-word', unicodeBidi: 'plaintext',
};

export default function ConsoleContactViolations({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const [filter, setFilter] = useState(OPEN);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setRows(null); setErr('');
    listViolations(filter)
      .then(setRows)
      .catch((e) => {
        console.error('[ConsoleContactViolations] load failed:', e?.message || e);
        setErr(e?.message || 'load failed');
        setRows([]);
      });
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const decide = async (r, action) => {
    if (action === 'warned') {
      const preview = WARNING_PREVIEW[isSupplierRole(r.user_role) ? 'supplier' : 'buyer'];
      const ok = window.confirm(isAr
        ? `سيصل هذا التحذير إلى ${personName(r)} بلغته:\n\n«${preview}»\n\nإرسال؟`
        : `${personName(r)} will receive this warning in their language:\n\n"${preview}"\n\nSend?`);
      if (!ok) return;
    }
    setBusy(r.id);
    try {
      await reviewViolation(r.id, action);
      // In the open list the row leaves; elsewhere it changes state in place.
      setRows((list) => (filter === OPEN
        ? list.filter((x) => x.id !== r.id)
        : list.map((x) => (x.id === r.id ? { ...x, review_status: action, reviewed_at: new Date().toISOString() } : x))));
    } catch (e) {
      console.error('[ConsoleContactViolations] review failed:', e?.message || e);
      alert(isAr ? `تعذّر الحفظ: ${e.message}` : `Could not save: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';
  const repeats = useMemo(() => (rows || []).filter((r) => Number(r.level) >= 2).length, [rows]);

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="reviews">
      <div className="ac-page" style={{ fontFamily: font, maxWidth: 900 }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{isAr ? 'مخالفات التواصل' : 'Contact violations'}</h1>
            <p className="ac-sub">
              {isAr
                ? 'أرقام وحسابات وروابط حاول أحد إرسالها، وأخفتها المنصّة قبل أن تصل. قرّر: خطأ كشف، أم تحذير رسمي.'
                : 'Numbers, handles and links someone tried to send, masked before they arrived. Decide: false positive, or a formal warning.'}
            </p>
          </div>
          <button className="ac-btn ac-btn-sm" onClick={load}>{isAr ? 'تحديث' : 'Refresh'}</button>
        </div>

        <div className="ac-chiprow" style={{ marginBottom: 18 }}>
          {FILTERS.map((f) => (
            <button key={f.key} className={`ac-chip${filter === f.key ? ' on' : ''}`} onClick={() => setFilter(f.key)}>
              {isAr ? f.ar : f.en}
              {filter === f.key && rows ? <span style={{ opacity: 0.6, marginInlineStart: 4 }}>{rows.length}</span> : null}
            </button>
          ))}
        </div>

        {err && <p style={{ color: 'var(--ac-danger)', fontSize: 13 }}>{err}</p>}
        {filter === OPEN && repeats > 0 && (
          <p className="ac-sub" style={{ marginBottom: 12 }}>
            {isAr ? `منها ${repeats} تكرار — نفس الشخص حاول أكثر من مرة.` : `${repeats} of these are repeats — the same person tried again.`}
          </p>
        )}

        {rows == null ? (
          <div className="ac-skel" style={{ height: 120 }} />
        ) : rows.length === 0 ? (
          <div className="ac-empty">{filter === OPEN
            ? (isAr ? 'لا مخالفات تنتظر المراجعة 👌' : 'Nothing waiting for review 👌')
            : (isAr ? 'لا شيء هنا.' : 'Nothing here.')}</div>
        ) : rows.map((r) => {
          const repeat = Number(r.level) >= 2;
          const st = STATUS_BADGE[r.review_status] || STATUS_BADGE.open;
          const who = personLink(r);
          const convo = conversationLink(r);
          return (
            <div key={r.id} className="ac-card" data-testid="violation" style={{ padding: 16, marginBottom: 10 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span className={`ac-badge ${repeat ? 'danger' : 'warn'}`}>
                  {repeat ? (isAr ? 'تكرار' : 'Repeat') : (isAr ? 'أول مرة' : 'First time')}
                </span>
                <span className="ac-badge neutral">{isSupplierRole(r.user_role) ? (isAr ? 'مورّد' : 'Supplier') : (isAr ? 'تاجر' : 'Trader')}</span>
                {who ? (
                  <button type="button" className="ac-link" onClick={() => nav(who)}
                    style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontWeight: 700, fontSize: 14, color: 'var(--ac-ink)', fontFamily: font }}>
                    {personName(r)}
                  </button>
                ) : <b style={{ fontSize: 14 }}>{personName(r)}</b>}
                <span style={{ fontSize: 12, color: 'var(--ac-faint)' }}>
                  {sourceLabel(r.source, isAr)} · {when(r.created_at, isAr)}
                  {r.user_events > 1 ? ` · ${isAr ? `${r.user_events} مخالفات لهذا الحساب` : `${r.user_events} on this account`}` : ''}
                </span>
                <span style={{ flex: 1 }} />
                <span className={`ac-badge ${st.tone}`}>{isAr ? st.ar : st.en}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: 11.5, fontWeight: 700, color: 'var(--ac-faint)' }}>{isAr ? 'ما كتبه (لا يراه غير الإدارة)' : 'What they wrote (admins only)'}</p>
                  <p style={{ ...textBox, background: 'var(--ac-danger-bg)', border: '1px solid var(--ac-danger-bd)' }}>{r.original}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: 11.5, fontWeight: 700, color: 'var(--ac-faint)' }}>{isAr ? 'ما وصل للطرف الآخر' : 'What was delivered'}</p>
                  <p style={{ ...textBox, background: 'var(--ac-hover)', border: '1px solid var(--ac-line-2)' }}>{r.masked}</p>
                </div>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                {convo && (
                  <button className="ac-btn ac-btn-sm" onClick={() => nav(convo)}>{isAr ? 'فتح المحادثة' : 'Open conversation'}</button>
                )}
                {r.review_status === OPEN && (
                  <>
                    <button className="ac-btn ac-btn-sm" disabled={busy === r.id} onClick={() => decide(r, 'dismissed')}>
                      {isAr ? 'تجاهل (خطأ كشف)' : 'Dismiss (false positive)'}
                    </button>
                    <button className="ac-btn ac-btn-sm ac-btn-primary" disabled={busy === r.id} onClick={() => decide(r, 'warned')}>
                      {isAr ? 'أرسل تحذير رسمي' : 'Send formal warning'}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </ConsoleShell>
  );
}
